import { useEffect, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate } from "react-router-dom"
import { RUSSIAN_LANGUAGE } from "../api/config"
import { describeError } from "../api/http"
import { keys } from "../api/keys"
import { groupWrites } from "../api/mutations"
import { useReservedGroupId } from "../api/queries"
import { useGroupRights } from "../auth/rights"
import { ActionNotes, Note } from "../components/Note"
import { useLanguageId } from "../lib/language"
import { useWriter } from "./group/useWriter"

/**
 * Создание группы.
 *
 * ERP выдаёт следующий свободный номер сама (get-or-reserve без id) и держит
 * его в памяти процесса, пока группу не создали. Номер можно заменить руками —
 * тогда он уходит в `id`, а зарезервированный остаётся в `autoId`: по нему бэк
 * находит бронь.
 *
 * Название принимается только на русском (ProductGroupController.cs:122-128);
 * переводы заводятся уже на вкладке «Описание» созданной карточки.
 */
export default function NewGroupScreen() {
	const navigate = useNavigate()
	const client = useQueryClient()
	const languageId = useLanguageId()
	const reserved = useReservedGroupId()
	const writer = useWriter(undefined)
	// прайс-группы у новой карточки ещё нет: она появится с первым товаром
	const rights = useGroupRights(null, RUSSIAN_LANGUAGE)

	const [id, setId] = useState("")
	const [name, setName] = useState("")
	const [isToolset, setIsToolset] = useState(false)

	useEffect(() => {
		if (reserved.data?.id) setId(reserved.data.id)
	}, [reserved.data?.id])

	const create = async () => {
		const autoId = reserved.data?.id
		if (!autoId || !name.trim() || !id.trim()) return
		const ok = await writer.run(() =>
			groupWrites.create({
				id: id.trim(),
				autoId,
				name: name.trim(),
				isToolset,
				languageId: RUSSIAN_LANGUAGE,
			})
		)
		if (!ok) return
		// бронь израсходована (GroupWriter.cs:122-123) — следующей группе нужен свой номер
		client.removeQueries({ queryKey: keys.reservedGroupId() })
		navigate(`/groups/${id.trim()}`, { replace: true })
	}

	if (!rights.createGroup) {
		return (
			<div className="state">
				Создавать группы может только контент-менеджер.
				<div>
					<Link to="/groups">← к списку групп</Link>
				</div>
			</div>
		)
	}

	return (
		<>
			<div className="page-head">
				<Link to="/groups" className="btn" title="К списку групп">
					←
				</Link>
				<div className="page-head__text">
					<h1>Новая группа</h1>
					<div className="page-head__meta">
						номер зарезервирован ERP, его можно заменить своим
					</div>
				</div>
			</div>

			<div className="panel panel--form">
				<div className="panel__body">
					<ActionNotes action={writer} />

					{reserved.isError && (
						<Note tone="error">
							Не удалось зарезервировать номер: {describeError(reserved.error)}
						</Note>
					)}

					<Note tone="hint">
						Название сохраняется на русском — этого требует ERP. Переводы на
						латышский и остальные языки заводятся после создания, на вкладке
						«Описание».
					</Note>

					<div className="field">
						<label className="field__label">Код группы</label>
						<input
							className="input mono"
							value={id}
							placeholder={reserved.isLoading ? "резервируем…" : ""}
							disabled={reserved.isLoading}
							onChange={event => setId(event.target.value)}
						/>
					</div>

					<div className="field">
						<label className="field__label">Название (русский)</label>
						<input
							className="input"
							value={name}
							autoFocus
							onChange={event => setName(event.target.value)}
							onKeyDown={event => {
								if (event.key === "Enter") void create()
							}}
						/>
					</div>

					<div className="checkline">
						<label>
							<input
								type="checkbox"
								checked={isToolset}
								onChange={event => setIsToolset(event.target.checked)}
							/>
							это набор инструментов
						</label>
					</div>

					<div className="pub-actions">
						<button
							className="btn btn--primary"
							disabled={
								writer.busy || !reserved.data || !name.trim() || !id.trim()
							}
							onClick={() => void create()}
						>
							Создать
						</button>
						<Link
							to="/groups"
							className="btn"
							title="Бронь номера снимет ERP — номер вернётся в оборот после перезапуска сервиса"
						>
							Отмена
						</Link>
					</div>

					{languageId !== RUSSIAN_LANGUAGE && (
						<p className="muted">
							После создания карточка откроется на выбранном языке — можно
							сразу вписать перевод названия.
						</p>
					)}
				</div>
			</div>
		</>
	)
}
